import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Globe, Zap, Database } from "lucide-react";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo-dark.svg";

interface AuthLayoutProps {
  children: ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation("auth");

  const features = [
    {
      icon: Shield,
      label: t("login.features.sso.label"),
      sub: t("login.features.sso.sub"),
    },
    {
      icon: Globe,
      label: t("login.features.regions.label"),
      sub: t("login.features.regions.sub"),
    },
    {
      icon: Zap,
      label: t("login.features.provision.label"),
      sub: t("login.features.provision.sub"),
    },
    {
      icon: Database,
      label: t("login.features.s3.label"),
      sub: t("login.features.s3.sub"),
    },
  ];

  return (
    // `min-h-dvh`, not `min-h-screen`: 100vh on mobile browsers is the height *without* the
    // retracted URL bar, so a 100vh auth screen is taller than what is actually visible.
    <div className="min-h-dvh grid grid-cols-1 lg:grid-cols-[1fr_520px] relative overflow-hidden bg-canvas">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative flex-col justify-between p-10 px-14 bg-canvas overflow-hidden">
        {/* Grid background with radial glow */}
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(var(--hairline) 1px, transparent 1px), linear-gradient(90deg, var(--hairline) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            mask: "radial-gradient(60% 60% at 40% 40%, black, transparent 80%)",
            WebkitMask:
              "radial-gradient(60% 60% at 40% 40%, black, transparent 80%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(800px 600px at 20% 30%, var(--brand-soft), transparent 60%), radial-gradient(700px 500px at 80% 80%, var(--info-soft), transparent 60%)",
          }}
        />

        {/* Top bar */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={logoLight}
              alt="WiServices"
              className="h-7 block dark:hidden"
            />
            <img
              src={logoDark}
              alt="WiServices"
              className="h-7 hidden dark:block"
            />
          </div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-panel border border-hairline text-xs text-muted">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ok opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-ok" />
            </span>
            {t("login.statusOk")}
          </span>
        </div>

        {/* Hero */}
        <div className="relative z-10 max-w-[480px]">
          <span className="inline-block font-mono text-[11px] tracking-[0.14em] uppercase px-2.5 py-1 rounded w-fit text-brand bg-brand-soft border border-brand-strong">
            {t("login.eyebrow")}
          </span>
          <h1 className="text-[52px] font-medium leading-[1.05] tracking-tight mt-5 mb-5 text-balance text-ink">
            {t("login.heroPre")}{" "}
            <span className="text-brand">{t("login.heroHighlight")}</span>
          </h1>
          <p className="text-[15px] leading-relaxed text-muted max-w-[440px]">
            {t("login.heroBody")}
          </p>
        </div>

        {/* Features grid */}
        <div className="relative z-10 grid grid-cols-2 gap-3 max-w-[520px]">
          {features.map((feat) => (
            <div
              key={feat.label}
              className="flex items-start gap-2.5 p-3 rounded-xl bg-canvas-2/30 border border-hairline backdrop-blur-sm"
            >
              <span className="flex items-center justify-center w-[30px] h-[30px] rounded-lg bg-brand-soft text-brand shrink-0">
                <feat.icon className="w-4 h-4" />
              </span>
              <div>
                <b className="text-sm font-medium block text-ink">
                  {feat.label}
                </b>
                <span className="text-xs text-muted">{feat.sub}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="relative z-10 flex gap-5 text-xs text-muted font-mono">
          <span>WiServices &copy; {new Date().getFullYear()}</span>
          <span className="ml-auto">{t("login.statusPage")}</span>
        </div>
      </div>

      {/* Right card. The divider only exists once the brand panel is beside it — below `lg` it
          was a hairline running down the left edge of the screen with nothing on the other side.
          40px of padding also left only 280px of usable width on a 360px phone. */}
      <div className="relative flex items-center justify-center bg-panel p-5 sm:p-8 lg:border-l lg:border-hairline lg:p-10">
        <div className="w-full max-w-[380px]">{children}</div>
      </div>
    </div>
  );
}
